import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { IconCircleInfoSolid } from '@/app/common/icons/libraryIcons';
import Image, { type ImageLoaderProps } from 'next/legacy/image';
import { animated, useSpring } from 'react-spring';
import { Text } from '@lmn/design-system';
import { useMeasure } from 'react-use';
import { Picture } from '@/app/components/hotel-detail/ImageWithFallback';
import { useTranslation } from '@/app/components/translations/Translation';
import Tooltip from '@/app/components/hotel-detail/Tooltip';
import { getGallerySourceOptimization } from '@/app/components/hotel-detail/getGallerySourceOptimization';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { TrustYouWidget } from '@/app/components/hotel-detail/trust-you/TrustYouWidget';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useFetchHotelStatic } from '../hooks/useFetchHotelStatic';
import ShareButton from '../ShareButton';
import { BackArrow, BackButtonWithArrow } from '../BackButton';
import { useIsDesktopOrGreater, useIsTabletOrGreater } from '../hooks/useMediaSSR';

type TSHGalleryProps = {
  images: string[] | [];
  discount?: number | undefined;
} & React.HTMLAttributes<HTMLElement>;

const imageLoader = ({ src, width, quality }: ImageLoaderProps) => `${src}?w=${width}&q=${quality || 75}`;

const StyledDiv = styled.div``;
const AnimatedDiv = animated(StyledDiv);

const TooltipWrapper = styled.div`
  .MuiTooltip-arrow {
    inset: auto auto auto 8px !important;
  }

  .MuiTooltip-tooltip {
    inset: auto auto -12px -16px !important;
    box-shadow: ${({ theme }) => theme.kallisto.shadow.largeDownShadow};
  }
`;

const TopSecretInfoBox = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  gap: 8px;
  background-color: ${(p) => p.theme.kallisto.color.background.product.backgroundTopSecretHotels};
  color: white;
`;

const TopSecretInfoTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export default function TSHGallery(props: TSHGalleryProps) {
  const { images, discount, ...rest } = props;
  const {
    commonData: { isApp },
  } = usePageWrapperData();
  const { localization } = usePageWrapperData();
  const { imageBasePath } = localization;
  const { reviewsDisabled } = localization.configValuesApp;
  const defaultImage = `${imageBasePath}TSH_placeholder_image.png`;
  const { isHotelStaticPage } = useInitialParams();
  const isTabletOrGreater = useIsTabletOrGreater();
  const isDesktopOrGreater = useIsDesktopOrGreater();
  const isMobile = isTabletOrGreater == null ? undefined : !isTabletOrGreater;
  const { data: hotelStaticData } = useFetchHotelStatic();
  const showBackArrow = !isApp && isMobile;

  const initialImagesWithSources = getGallerySourceOptimization({
    images: images.slice(0, 1),
    isMobile,
    isTopSecret: true,
    totalImages: 1,
  });

  return (
    <div {...rest}>
      <div
        css={css`
          height: 260px;
          position: relative;
          overflow: hidden;

          @media ${customCss.breakpoints.sm} {
            height: 300px;
            border-radius: 8px;
          }
          @media ${customCss.breakpoints.xl} {
            height: 400px;
          }
        `}
      >
        {isMobile && !isHotelStaticPage ? (
          <div
            css={css`
              position: absolute;
              top: 0;
              right: 0;
              padding: 16px;
              z-index: 2;
            `}
          >
            <ShareButton hotelName={hotelStaticData?.name || ''} className="info-share" style={{ gridArea: 'share' }} />
          </div>
        ) : null}
        {showBackArrow && (
          <div
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              padding: 16px;
              z-index: 2;
            `}
          >
            <BackButtonWithArrow
              solidIcon={true}
              css={css`
                ${BackArrow} {
                  display: flex;
                  justify-content: center;
                  border-radius: 50%;
                  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
                  width: 32px;
                  height: 32px;

                  & svg,
                  & [data-library-icon] {
                    width: 16px;
                    height: 16px;
                  }
                }
              `}
            />
          </div>
        )}

        {!initialImagesWithSources.length ? (
          <Image
            src={defaultImage}
            alt="top secret"
            layout="fill"
            priority={true}
            loader={imageLoader}
            css={css`
              object-fit: cover;
            `}
          />
        ) : (
          initialImagesWithSources.map((image) => (
            <Picture
              alt="top secret"
              url={image.url}
              key={image.url}
              sources={image.sources}
              loading="lazy"
              css={`
                width: 100%;
                height: 100%;
                object-fit: cover;
                transition: transform 1s;
                transform: scale3d(1, 1, 1);
                cursor: pointer;
              `}
            />
          ))
        )}
        <div
          css={`
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          `}
        >
          {}
          <img
            css={`
              width: 96px;
              height: 96px;

              @media ${customCss.breakpoints.sm} {
                width: 120px;
                height: 120px;
              }
            `}
            src={`${imageBasePath}logo-image-tsh-white.svg`}
            alt="tsh-icon-logo"
          />
        </div>
        {!isMobile && <TopSecretOverlay discount={discount} />}
        {/* The widget self-guards: TA renders from the rating, TY needs its key, any other source renders nothing. */}
        {isDesktopOrGreater && !reviewsDisabled && hotelStaticData?.hotelOpinion ? (
          <TrustYouWidget
            css={css`
              position: absolute;
              top: 0;
            `}
            rating={hotelStaticData.hotelOpinion.rating}
            reviews={hotelStaticData.hotelOpinion.numberOfReviews}
            trustYouId={hotelStaticData.hotelOpinion.opinionId}
            opinionCategories={hotelStaticData.hotelOpinion.categories}
            reviewSource={hotelStaticData.hotelOpinion.reviewSource}
            showDetails={true}
            isOpen={true}
          />
        ) : undefined}
      </div>
      {isMobile && <TopSecretInfoMobile discount={discount} />}
    </div>
  );
}

function TopSecretOverlay(props: { discount?: number }) {
  const { discount } = props;
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { imageBasePath } = localization;
  const [ref, { width }] = useMeasure();
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (discount && !shouldAnimate) {
      setTimeout(() => setShouldAnimate(true), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discount]);

  const discountPaddingRight = 16;

  const style = useSpring({
    from: {
      opacity: 0,
      width: 0,
    },
    to: {
      opacity: shouldAnimate ? 1 : 0,
      width: shouldAnimate ? width + discountPaddingRight : 0,
    },
    config: {
      duration: 200,
    },
  });

  return (
    <div
      css={css`
        display: flex;
        flex-wrap: wrap-reverse;
        row-gap: 8px;
        justify-content: flex-end;
        bottom: 0;
        position: absolute;
        padding: 24px 0 15px;
        width: 100%;
        z-index: 0;

        @media ${customCss.breakpoints.sm} {
          justify-content: flex-end;
        }

        &::after {
          border-right: 1500px solid ${(p) => p.theme.kallisto.color.background.product.backgroundTopSecretHotels};
          border-top: 72px solid transparent;
          content: '';
          display: block;
          height: 250px;
          position: absolute;
          right: 0;
          top: 0;
          z-index: -1;
        }
      `}
    >
      {}
      <img
        css={`
          max-width: 300px;
          margin-right: 16px;
          height: 21px;
          @media ${customCss.breakpoints.sm} {
            height: 28px;
          }
        `}
        src={`${imageBasePath}logo-tsh.svg`}
        alt="tsh-label-logo"
      />

      <AnimatedDiv style={style}>
        <TooltipWrapper>
          <Tooltip
            css={css`
              ${({ theme }) => theme.kallisto.mixins.font('Title5')};
              display: flex;
              color: ${(p) => p.theme.kallisto.color.text.interactive.textPrimaryDefault};
            `}
            label={t('vacation.common.topsecret.discount.description', { discount: discount?.toString() || '' })}
            maxWidth={220}
          >
            <div
              ref={ref as React.Ref<HTMLDivElement>}
              css={css`
                display: flex;
                align-items: center;
                overflow: ${discount ? 'initial' : 'hidden'};
                padding: 4px 0;
                border-radius: 4px;
                background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
                white-space: nowrap;
              `}
            >
              <span
                css={`
                  padding-left: 8px;
                `}
              >
                {t('vacation.common.topsecret.discount', { discount: discount?.toString() || '' })}
              </span>
              <IconCircleInfoSolid
                css={css`
                  font-size: ${(p) => p.theme.kallisto.font.size['200']};
                  margin: 0 0.5rem;
                `}
              />
            </div>
          </Tooltip>
        </TooltipWrapper>
      </AnimatedDiv>
    </div>
  );
}

function TopSecretInfoMobile(props: { discount?: number }) {
  const { discount } = props;
  const { t } = useTranslation();
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const { localization } = usePageWrapperData();
  const { imageBasePath } = localization;

  useEffect(() => {
    if (discount && !shouldAnimate) {
      setShouldAnimate(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discount]);

  const styleOnlyOpacity = useSpring({
    from: {
      opacity: 0,
    },
    to: {
      opacity: shouldAnimate ? 1 : 0,
    },
    config: {
      duration: 400,
    },
  });

  return (
    <TopSecretInfoBox>
      <TopSecretInfoTitleContainer>
        {}
        <img
          css={`
            max-width: 300px;
            height: 20px;
            @media ${customCss.breakpoints.sm} {
              height: 28px;
            }
          `}
          src={`${imageBasePath}logo-tsh.svg`}
          alt="tsh-label-logo"
        />
        {discount !== 0 && (
          <AnimatedDiv style={styleOnlyOpacity}>
            <div
              css={css`
                display: flex;
                align-items: center;
                padding: 4px 8px;
                border-radius: 4px;
                background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
                white-space: nowrap;
              `}
            >
              <Text
                variant="Title5"
                css={css`
                  color: ${(p) => p.theme.kallisto.color.text.interactive.textPrimaryDefault};
                `}
              >
                {`${t('vacation.common.topsecret.discount', {
                  discount: discount?.toString() || '',
                })}*`}
              </Text>
            </div>
          </AnimatedDiv>
        )}
      </TopSecretInfoTitleContainer>
      {discount !== 0 && (
        <Text variant="BodySmall">
          <AnimatedDiv style={styleOnlyOpacity}>
            {`** ${t('vacation.common.topsecret.discount.description', { discount: discount?.toString() || '' })}`}
          </AnimatedDiv>
        </Text>
      )}
    </TopSecretInfoBox>
  );
}
